import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CopyableField,
  DefinitionRow,
  EntityHeader,
  Stack,
  Typography,
} from '@cere/cere-design-system';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import { ensureVaultDelegateGrant } from '@/lib/clients/vault/vault-delegate';
import { useConfig } from '@/src/config/context';

interface Props {
  wallet: CereWallet;
  vaultId: string;
  vaultBaseUrl: string | null;
  /** Whether the vault's write-delegation grant is known to be registered.
   *  The layout owns this single source of truth; SettingsPage renders from this
   *  prop rather than a local copy so a Settings-side re-authorize and the
   *  disconnected banner stay in sync (no state drift on prop updates). */
  delegateActive: boolean;
  /** Reports a successful re-authorize up to the layout so it can update
   *  vault.status — making the disconnected banner disappear immediately. */
  onDelegateChange: (active: boolean) => void;
  onDisconnect: () => void;
}

export function SettingsPage({ wallet, vaultId, vaultBaseUrl, delegateActive, onDelegateChange, onDisconnect }: Props) {
  const config = useConfig();
  const displayName = wallet.name ?? wallet.email ?? 'Vault owner';

  // Render straight from the prop — the layout is the source of truth. A local
  // useState seed would ignore prop updates (e.g. a banner-side re-authorize)
  // and drift out of sync.
  const granted = delegateActive;
  const [busy, setBusy] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  // Re-grant vault-api's on-chain NonTransfer proxy — idempotent (no
  // transaction when a satisfying grant is already on chain). This is the
  // durable replacement for the removed gateway delegate-grant
  // (`registerVaultDelegate`); there is deliberately no revoke action here;
  // removing the proxy is a `Proxy.remove_proxy` extrinsic the SDK does not
  // wrap yet.
  async function reauthorize() {
    if (!vaultBaseUrl) { setGrantError('Vault API endpoint unavailable.'); return; }
    if (!config.rpcEndpoint) { setGrantError('Chain RPC endpoint unavailable.'); return; }
    setBusy(true);
    setGrantError(null);
    try {
      await ensureVaultDelegateGrant({
        signer: wallet.signingSigner,
        chainUrl: config.rpcEndpoint,
        vaultApiUrl: vaultBaseUrl,
      });
      onDelegateChange(true);
    } catch (e) {
      setGrantError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: { xs: 2, md: 3.5 }, pt: { xs: 3, md: 3 }, pb: { xs: 6, md: 7.5 } }}>
      <EntityHeader
        title="Settings"
        subtitle="Inspect your wallet, copy keys, manage your session."
      />

      <Section
        eyebrow="Identity"
        title="Wallet"
        description="Signed in with your passkey."
      >
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'primary.light', color: 'primary.contrastText',
                  fontWeight: 700, fontSize: 16, textTransform: 'uppercase',
                }}
              >
                {(displayName || wallet.address).slice(0, 2)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{displayName}</Typography>
                {wallet.email && (
                  <Typography variant="body2" color="text.secondary">{wallet.email}</Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                <Chip label="Embedded wallet" variant="default" size="small" />
                <Chip label="connected" variant="success" size="small" />
              </Stack>
            </Stack>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <CopyableField label="Address" value={wallet.address} />
              <CopyableField label="Public key" value={wallet.publicKeyHex} />
              <DefinitionRow label="Signature" value="ed25519" divider={false} />
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="primary"
                size="small"
                onClick={onDisconnect}
                sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
              >
                Logout
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Section>

      <Section
        eyebrow="Connection"
        title="API"
        description="Vault endpoint this app talks to."
      >
        <Card>
          <CardContent>
            <Stack spacing={0}>
              <DefinitionRow label="Vault id" value={<Typography variant="mono">{vaultId}</Typography>} />
              <DefinitionRow label="Base url" value={<Typography variant="mono">{vaultBaseUrl ?? '—'}</Typography>} />
              <DefinitionRow label="Auth" value="X-Public-Key + X-Signature (ed25519)" divider={false} />
            </Stack>
          </CardContent>
        </Card>
      </Section>

      <Section
        eyebrow="Storage"
        title="Write delegation"
        description="The on-chain proxy that lets your Vault store data on your behalf."
      >
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1">Your Vault may store data on your behalf</Typography>
                <Typography variant="body2" color="text.secondary">
                  {granted
                    ? 'Granted — your Vault can write storage on your behalf until you revoke the proxy.'
                    : 'Not confirmed for this device. Re-authorize to re-grant the proxy so your Vault can write on your behalf.'}
                </Typography>
              </Box>
              <Chip label={granted ? 'active' : 'unconfirmed'} variant={granted ? 'success' : 'default'} size="small" />
              <Button variant="primary" size="small" onClick={() => { void reauthorize(); }} disabled={busy}>
                Re-authorize
              </Button>
            </Stack>
            {grantError && (
              <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{grantError}</Typography>
            )}
          </CardContent>
        </Card>
      </Section>
    </Box>
  );
}

function Section({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Box component="section">
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="eyebrow">{eyebrow}</Typography>
        <Typography variant="h6">{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}
